import type { AbilityKey, Family, StatusEffect, Weather } from './types'

// ---------------------------------------------------------------------------
// Abilities — passifs de combat. Plusieurs par famille (variété façon Pokémon),
// tirées du seed à la génération → déterministes, donc valables en arène classée
// et rejouables. Data-driven: chaque ability déclare ses effets numériques, le
// moteur les applique via les hooks ci-dessous. Inclut des IMMUNITÉS de statut.
// ---------------------------------------------------------------------------

export interface AbilityDef {
  key: AbilityKey
  name: string
  desc: string
  emoji: string
  // --- hooks (tous optionnels) ---
  /** Multiplicateur sur les dégâts SUBIS (0.88 = -12%). */
  dmgTaken?: number
  /** Multiplicateur additionnel sur les dégâts SUBIS des SPÉCIALES. */
  dmgTakenSpecial?: number
  /** Multiplicateur sur les dégâts INFLIGÉS. */
  dmgDealt?: number
  /** Multiplicateur sur les dégâts infligés quand HP > 50%. */
  dmgDealtHighHp?: number
  /** Multiplicateur sur les dégâts infligés quand HP < 40%. */
  dmgDealtLowHp?: number
  /** Énergie regagnée en plus par tour. */
  energyPerTurn?: number
  /** % des HP max régénérés par tour. */
  hpRegenPct?: number
  /** Probabilité d'agir deux fois dans le tour. */
  doubleChance?: number
  /** Énergie volée à l'ennemi à chaque coup porté. */
  energyDrain?: number
  /** % des dégâts infligés soigné à l'attaquant (drain passif). */
  hpDrainPct?: number
  /** Bonus de chance de critique. */
  critChanceBonus?: number
  /** Multiplicateur de dégâts des critiques (override). */
  critMult?: number
  /** Statuts dont le porteur est TOTALEMENT immunisé. */
  statusImmune?: StatusEffect[]
  /** Multiplicateur sur la proba de subir un effet de statut (résistance). */
  statusResist?: number
  /** Immunité au vol d'énergie adverse. */
  drainImmune?: boolean
  /** Priorité ajoutée aux coups normaux (gratuits). */
  priorityNormal?: number
  /** Multiplicateur de vitesse (ordre de tour). */
  speedMult?: number
  /** Dégâts subis en plus (contrepartie d'un gros bonus offensif). */
  selfDmgTakenExtra?: number
  // --- on-entry / météo ---
  /** À l'entrée: baisse le palier d'ATK de l'adversaire actif (Intimidate). */
  onEntryLowerEnemyAtk?: number
  /** À l'entrée: installe une météo. */
  onEntryWeather?: Weather
  /** Bonus de dégâts si la météo courante correspond. */
  weatherBoost?: { weather: Weather; mult: number }
}

export const ABILITIES: Record<AbilityKey, AbilityDef> = {
  // Bitcoin — le tank
  hard_cap: { key: 'hard_cap', name: 'Hard Cap', emoji: '🛡', desc: 'Réduit tous les dégâts subis de 12%.', dmgTaken: 0.88 },
  diamond_hands: { key: 'diamond_hands', name: 'Diamond Hands', emoji: '💎', desc: 'Immunité au gel ; dégâts subis -6%.', statusImmune: ['freeze'], dmgTaken: 0.94 },
  cold_storage: { key: 'cold_storage', name: 'Cold Storage', emoji: '❄️', desc: 'Immunisé au vol d’énergie ; dégâts subis -8%.', drainImmune: true, dmgTaken: 0.92 },
  intimidate: { key: 'intimidate', name: 'Intimidate', emoji: '😤', desc: 'À l’entrée, baisse l’ATK de l’adversaire (-1 palier).', onEntryLowerEnemyAtk: 1 },
  // Ethereum — le mage
  gas_refund: { key: 'gas_refund', name: 'Gas Refund', emoji: '⛽', desc: 'Recharge +8 énergie à chaque tour.', energyPerTurn: 8 },
  smart_contract: { key: 'smart_contract', name: 'Smart Contract', emoji: '📜', desc: 'Les effets de statut ennemis ont 50% de chance en moins.', statusResist: 0.5 },
  proof_of_stake: { key: 'proof_of_stake', name: 'Proof of Stake', emoji: '🔒', desc: 'Dégâts +12% tant que les HP sont au-dessus de 50%.', dmgDealtHighHp: 1.12 },
  bull_call: { key: 'bull_call', name: 'Bull Call', emoji: '🐂', desc: 'À l’entrée, installe un Bull Market (+12% dégâts).', onEntryWeather: 'bull' },
  // Base — la vitesse
  low_gas: { key: 'low_gas', name: 'Low Gas', emoji: '⚡', desc: '26% de chance d’agir deux fois dans le tour.', doubleChance: 0.26 },
  sequencer: { key: 'sequencer', name: 'Sequencer', emoji: '⏩', desc: 'Les coups normaux gagnent +1 de priorité.', priorityNormal: 1 },
  rollup: { key: 'rollup', name: 'Rollup', emoji: '🌀', desc: '+15% de vitesse.', speedMult: 1.15 },
  momentum: { key: 'momentum', name: 'Momentum', emoji: '📊', desc: 'Dégâts +18% en Bull Market.', weatherBoost: { weather: 'bull', mult: 1.18 } },
  // DeFi — le vampire
  yield_drain: { key: 'yield_drain', name: 'Yield Drain', emoji: '🩸', desc: 'Vole 10 énergie à l’ennemi à chaque coup porté.', energyDrain: 10 },
  vampiric: { key: 'vampiric', name: 'Vampiric', emoji: '🦇', desc: 'Soigne 12% des dégâts infligés.', hpDrainPct: 0.12 },
  leverage: { key: 'leverage', name: 'Leverage', emoji: '📈', desc: 'Dégâts +15%, mais dégâts subis +8%.', dmgDealt: 1.15, selfDmgTakenExtra: 0.08 },
  // Meme — le chaos
  to_the_moon: { key: 'to_the_moon', name: 'To The Moon', emoji: '🚀', desc: '+18% de chance de critique, critiques ×2.2.', critChanceBonus: 0.18, critMult: 2.2 },
  diamond_paws: { key: 'diamond_paws', name: 'Diamond Paws', emoji: '🐾', desc: 'Trop hypé pour dormir : immunité au sommeil.', statusImmune: ['sleep'] },
  fomo: { key: 'fomo', name: 'FOMO', emoji: '🔥', desc: 'Dégâts +18% quand les HP passent sous 40%.', dmgDealtLowHp: 1.18 },
  storm_caller: { key: 'storm_caller', name: 'Storm Caller', emoji: '🌊', desc: 'À l’entrée, déchaîne la Volatilité (+15% crit).', onEntryWeather: 'volatile' },
  // Stablecoin — le soigneur
  peg_stable: { key: 'peg_stable', name: 'Peg Stable', emoji: '🟢', desc: 'Régénère 3% des HP max à chaque tour.', hpRegenPct: 0.03 },
  audited: { key: 'audited', name: 'Audited', emoji: '✅', desc: 'Immunité au poison et à la brûlure.', statusImmune: ['poison', 'burn'] },
  reserve: { key: 'reserve', name: 'Full Reserve', emoji: '🏦', desc: 'Réduit de 15% les dégâts subis des spéciales.', dmgTakenSpecial: 0.85 },
}

// Pool d'abilities par famille — la 1ère est la « signature » (le passif historique).
export const ABILITY_POOL: Record<Family, AbilityKey[]> = {
  bitcoin: ['hard_cap', 'diamond_hands', 'cold_storage', 'intimidate'],
  ethereum: ['gas_refund', 'smart_contract', 'proof_of_stake', 'bull_call'],
  base: ['low_gas', 'sequencer', 'rollup', 'momentum'],
  defi: ['yield_drain', 'vampiric', 'leverage'],
  meme: ['to_the_moon', 'diamond_paws', 'fomo', 'storm_caller'],
  stable: ['peg_stable', 'audited', 'reserve'],
}

export function abilityDef(key: AbilityKey | undefined, family: Family): AbilityDef {
  return ABILITIES[key ?? ABILITY_POOL[family][0]]
}
